'use client';

import { useCallback, useMemo } from 'react';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';

type QueryEnumParamOptions<T extends string> = {
  /** URL 上的參數名稱，例如 'tabPage' */
  paramName: string;
  /** 允許的字串值列表，例如 ['give', 'buy'] as const */
  validValues: readonly T[];
  /** 找不到或不合法時的預設值 */
  defaultValue: T;
};

export function useQueryEnumParam<T extends string>({
  paramName,
  validValues,
  defaultValue,
}: QueryEnumParamOptions<T>): [T, (next: T) => void] {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const raw = searchParams.get(paramName);

  // 解析目前 URL 上的值 → 變成型別安全的 T
  const value = useMemo<T>(() => {
    if (!raw) return defaultValue;
    return (validValues as readonly string[]).includes(raw) ? (raw as T) : defaultValue;
  }, [raw, validValues, defaultValue]);

  // 更新 URL 上的參數，並保持其他 query 不變
  const setValue = useCallback(
    (next: T) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set(paramName, next);
      router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    },
    [paramName, router, pathname, searchParams]
  );

  return [value, setValue];
}
